/**
 * =============================================================================
 * ROASTBREW - RESET PASSWORD PAGE
 * =============================================================================
 *
 * Public page rendered at /reset-password?token=xxx
 * User arrives here from the reset email link.
 *
 * THEME: pre-auth, so it takes its colours from publicTheme.js — not
 * ThemeContext, which has no tier to read before sign-in. See that file.
 *
 * =============================================================================
 */

import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { T, PublicTheme } from './publicTheme';
import PublicMark from './PublicMark';


export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { API_URL } = useAuth();

  const token = searchParams.get('token');

  const [status, setStatus] = useState(token ? 'form' : 'error');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(
    token ? '' : 'No reset token found. Please check your email link.'
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Password reset failed');
      }

      setStatus('success');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="rb-public"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <PublicTheme />
      <div className="rb-ambient" />

      <PublicMark style={{ marginBottom: 'var(--s-5)' }} />

      <div
        className="rb-enter"
        style={{
          textAlign: 'center', maxWidth: 'var(--m-form)', width: '100%',
          position: 'relative', zIndex: 1,
        }}
      >

        {status === 'form' && (
          <>
            <h1 className="rb-h">Set a new password</h1>
            <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
              Choose something you haven&rsquo;t used before. At least 8 characters.
            </p>

            {error && (
              <div className="rb-alert rb-alert--error" style={{ marginBottom: 'var(--s-3)' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="rb-field">
                <label className="rb-label" htmlFor="rb-new-password">New password</label>
                <input
                  id="rb-new-password"
                  className="rb-input"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>

              <div className="rb-field" style={{ marginBottom: 'var(--s-4)' }}>
                <label className="rb-label" htmlFor="rb-confirm-password">Confirm password</label>
                <input
                  id="rb-confirm-password"
                  className="rb-input"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                className="rb-btn rb-btn--gold rb-btn--block"
                disabled={isLoading}
                data-busy={isLoading ? 'true' : undefined}
              >
                {isLoading ? 'Resetting' : 'Reset password'}
              </button>
            </form>
          </>
        )}

        {status === 'success' && (
          <>
            <StatusGlyph tone="success" />
            <h1 className="rb-h">Password reset</h1>
            <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
              You can now sign in with your new password.
            </p>
            <button className="rb-btn rb-btn--gold" onClick={() => navigate('/')}>
              Go to sign in
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <StatusGlyph tone="error" />
            <h1 className="rb-h">This link has expired</h1>
            <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
              {error || 'Request a new password reset link and try again.'}
            </p>
            <button className="rb-btn rb-btn--ghost" onClick={() => navigate('/')}>
              Go to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}


// =============================================================================
// STATUS GLYPH
// =============================================================================
// A tinted disc carrying a tick or a cross. Matches VerifyEmailPage, which is
// the screen most users see immediately before this one.

function StatusGlyph({ tone }) {
  const color = tone === 'success' ? T.success : T.danger;

  return (
    <div style={{
      width: '72px', height: '72px', borderRadius: '50%',
      background: `${color}12`, border: `.5px solid ${color}38`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto var(--s-3)',
    }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {tone === 'success'
          ? <path d="M20 6L9 17l-5-5" />
          : <><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></>}
      </svg>
    </div>
  );
}
